import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { Button } from "@/shared/ui";
import { CATEGORY_CONFIG, CATEGORY_ORDER } from "../consts/categories";
import type { NotificationCategory } from "../types";

interface Props {
  activeCategory: NotificationCategory | null;
  onCategoryChange: (category: NotificationCategory | null) => void;
  className?: string;
}

export default function NotificationCategoryFilter({ activeCategory, onCategoryChange, className }: Props) {
  const { t } = useTranslation("Notifications");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(function attachWheelScroll() {
    const el = containerRef.current;
    if (!el) return;

    function onWheel(e: WheelEvent) {
      if (!el) return;
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    }

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <div ref={containerRef} className={cn("flex gap-2 overflow-x-auto no-scrollbar", className)}>
      <Button
        variant={activeCategory === null ? "primary" : "secondary"}
        size="md"
        onClick={() => onCategoryChange(null)}
      >
        {t("filter.all")}
      </Button>
      {CATEGORY_ORDER.map(function renderCategoryChip(category) {
        const config = CATEGORY_CONFIG[category];
        const ChipIcon = config.chipIcon;
        return (
          <Button
            key={category}
            variant={activeCategory === category ? "primary" : "secondary"}
            size="md"
            onClick={() => onCategoryChange(category)}
          >
            <ChipIcon size={16} color="currentColor" />
            {t(config.labelKey)}
          </Button>
        );
      })}
    </div>
  );
}
